import SidePanel from '../SidePanel.jsx';
import Loading from '../Loading';
import {
    setAssignablePackageGrantsQuery,
    useAssignablePackageGrantsQuery,
    usePackageGrantsQuery,
} from '../../lib/Queries';
import { Link } from 'react-router-dom';
import { getBusinessUrl } from '../../lib/useBusinessNavigate.js';
import { humanGrantRedeemableAmount } from '../../lib/PackageHelpers.js';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import { useEffect, useState } from 'react';
import * as Toast from '../../lib/Toast.js';
import EditGrantPanel from './EditGrantPanel.jsx';
import { formatDateShort } from '~/lib/DateTime.js';

const PackageGrantRow = ({ grant, contentRight, className }) => {
    return (
        <div
            className={`py-3 ml-4 flex items-center justify-between ${className}`}
        >
            <div>
                {grant.package.name}
                <div className="text-gray-500 text-sm">
                    <div>{humanGrantRedeemableAmount(grant)} remaining</div>
                    {grant.expires_at && (
                        <div>Expires {formatDateShort(grant.expires_at)}</div>
                    )}
                </div>
            </div>
            {contentRight}
        </div>
    );
};

const AssignPackagePanel = ({ isOpen, onClose, event, refetch }) => {
    const objectType = 'bookingevent';
    const { data, isLoading } = useAssignablePackageGrantsQuery(
        objectType,
        isOpen ? event?.id : 0,
    );
    const { save: saveAssign, isSaving: isAssigning } = useBusinessPostApi(
        `/packages/grants/assign`,
    );
    const { save: saveUnassign, isSaving: isUnassigning } = useBusinessPostApi(
        `/packages/grants/unassign`,
    );

    const [selectedGrant, setSelectedGrant] = useState(null);
    const [viewingGrant, setViewingGrant] = useState(null);

    const eventGrants = data?.used || [];
    const assignableGrants = (data?.assignable || []).filter(
        grant => !eventGrants.find(g => g.id === grant.id),
    );

    function doAssign(grant) {
        // Assign this grant to this event
        setSelectedGrant(grant?.id);

        saveAssign({
            grant: grant.id,
            objectType: objectType,
            objectId: event.id,
        })
            .then(result => {
                Toast.success('Successfully used package');
                refetch();
                setAssignablePackageGrantsQuery(objectType, event.id, result);
            })
            .finally(() => {
                setSelectedGrant(null);
            });
    }

    function doRemove(grant) {
        saveUnassign({
            grant: grant.id,
            objectType: objectType,
            objectId: event.id,
        })
            .then(result => {
                Toast.success('Successfully removed package');
                refetch();
                setAssignablePackageGrantsQuery(objectType, event.id, result);
            })
            .finally(() => {
                setSelectedGrant(null);
            });
    }

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title="Use Package for Event"
        >
            {isLoading && <Loading />}
            {!isLoading && eventGrants?.length > 0 && (
                <div>
                    <div className="panel-label">Applied</div>
                    <div className="panel pr-4 mb-6 divide-y">
                        {eventGrants.map(grant => (
                            <PackageGrantRow
                                grant={grant}
                                key={grant.id}
                                contentRight={
                                    <button
                                        type="button"
                                        className="btn-pink-pill-sm uppercase ml-4"
                                        onClick={() => doRemove(grant)}
                                    >
                                        {isUnassigning
                                            ? 'Removing...'
                                            : 'Remove'}
                                    </button>
                                }
                            />
                        ))}
                    </div>
                </div>
            )}

            {!isLoading && assignableGrants?.length > 0 && (
                <div>
                    <div className="panel-label">Applicable</div>
                    <div className="panel pr-4 divide-y">
                        {!!assignableGrants &&
                            assignableGrants.map(grant => (
                                <PackageGrantRow
                                    grant={grant}
                                    key={grant.id}
                                    contentRight={
                                        <button
                                            type="button"
                                            className="btn-light-pill-sm uppercase"
                                            onClick={() => doAssign(grant)}
                                        >
                                            {isAssigning &&
                                            selectedGrant === grant.id
                                                ? 'Assigning...'
                                                : 'Use'}
                                        </button>
                                    }
                                />
                            ))}
                    </div>
                </div>
            )}

            {!assignableGrants?.length &&
                !eventGrants?.length &&
                !isLoading && (
                    <div className="panel px-4 py-3">
                        <div className="mb-2">
                            This client does not have any packages that can be
                            used with "{event?.event_type?.name}
                            ". Please create a package or assign one
                        </div>
                        <Link
                            to={getBusinessUrl('/packages')}
                            className="no-underline"
                        >
                            Go to Packages →
                        </Link>
                    </div>
                )}
            <EditGrantPanel
                isOpen={!!viewingGrant}
                onClose={() => setViewingGrant(null)}
                grant={viewingGrant}
            />
        </SidePanel>
    );
};

export default AssignPackagePanel;
